export function clamp(value, min = 0, max = Number.POSITIVE_INFINITY) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return min;
  return Math.min(Math.max(parsed, min), max);
}

export function promotionFactor(promotions, stackingMode) {
  const active = promotions
    .filter((promotion) => promotion.active)
    .map((promotion) => clamp(promotion.discount, 0, 100) / 100);

  if (active.length === 0) return 1;
  if (stackingMode === "best") return 1 - Math.max(...active);
  return active.reduce((factor, discount) => factor * (1 - discount), 1);
}

export function calculatePricing(input) {
  const vatRate = clamp(input.accommodationVatRate, 0, 100) / 100;
  const commissionRate = clamp(input.commissionRate, 0, 100) / 100;
  const commissionVatRate = clamp(input.commissionVatRate, 0, 100) / 100;
  const nonRecoverableShare = clamp(input.nonRecoverableShare, 0, 100) / 100;
  const commissionableShare = clamp(input.commissionableShare, 0, 100) / 100;
  const promoFactor = promotionFactor(input.promotions, input.stackingMode);
  const vatFraction = input.vatRegistered ? vatRate / (1 + vatRate) : 0;
  const commissionBasisFactor =
    input.commissionBasis === "excludingVat" ? 1 / (1 + vatRate) : 1;
  const commissionCostFactor =
    commissionBasisFactor *
    commissionableShare *
    commissionRate *
    (1 + commissionVatRate * nonRecoverableShare);
  const retentionFactor = 1 - vatFraction - commissionCostFactor;

  let publicRate;
  let guestAccommodationPrice;

  if (input.direction === "target") {
    const targetTakeHome = clamp(input.targetTakeHome);
    guestAccommodationPrice =
      retentionFactor > 0 ? targetTakeHome / retentionFactor : 0;
    publicRate = promoFactor > 0 ? guestAccommodationPrice / promoFactor : 0;
  } else {
    publicRate = clamp(input.publicRate);
    guestAccommodationPrice = publicRate * promoFactor;
  }

  const promotionSavings = publicRate - guestAccommodationPrice;
  const accommodationVat = input.vatRegistered
    ? guestAccommodationPrice * vatFraction
    : 0;
  const priceExcludingVat = guestAccommodationPrice - accommodationVat;
  const rawCommissionBasis =
    input.commissionBasis === "excludingVat"
      ? guestAccommodationPrice / (1 + vatRate)
      : guestAccommodationPrice;
  const commissionBasis = rawCommissionBasis * commissionableShare;
  const bookingCommission = commissionBasis * commissionRate;
  const commissionVat = bookingCommission * commissionVatRate;
  const commissionVatCost = commissionVat * nonRecoverableShare;
  const takeHome =
    guestAccommodationPrice -
    accommodationVat -
    bookingCommission -
    commissionVatCost;

  const nights = Math.max(1, Math.round(clamp(input.nights, 1, 365)));
  const guests = Math.max(1, Math.round(clamp(input.guests, 1, 100)));
  const touristTaxPerGuestNight = clamp(input.touristTaxPerGuestNight);
  const touristTax = touristTaxPerGuestNight * nights * guests;

  return {
    publicRate,
    promoFactor,
    effectiveDiscount: 1 - promoFactor,
    promotionSavings,
    guestAccommodationPrice,
    accommodationVat,
    priceExcludingVat,
    commissionBasis,
    bookingCommission,
    commissionVat,
    commissionVatCost,
    takeHome,
    retentionFactor,
    nights,
    guests,
    stayAccommodationTotal: guestAccommodationPrice * nights,
    touristTax,
    guestStayTotal: guestAccommodationPrice * nights + touristTax,
    stayTakeHome: takeHome * nights
  };
}

export function calculateScenarios(input) {
  const targetNet = clamp(input.targetNet);
  const vatRate = clamp(input.accommodationVatRate, 0, 100) / 100;
  const touristTaxRate = clamp(input.touristTaxRate, 0, 100) / 100;
  const commissionRate = clamp(input.commissionRate, 0, 100) / 100;
  const commissionVatRate = clamp(input.commissionVatRate, 0, 100) / 100;
  const recoverableShare = clamp(input.commissionVatRecoverable, 0, 100) / 100;
  const nonRecoverableShare = 1 - recoverableShare;
  const promotionMultiplier = promotionFactor(
    input.promotions ?? [],
    input.stackingMode ?? "best"
  );
  const nights = Math.max(1, Math.round(clamp(input.nights, 1, 365)));
  const commissionCostMultiplier =
    commissionRate * (1 + commissionVatRate * nonRecoverableShare);

  function buildScenario({
    key,
    listRate,
    discountedNet,
    discountedGross,
    accommodationVat,
    touristTax,
    commissionBasis,
    bookingCommission,
    commissionVatCost,
    hotelNet
  }) {
    const clientNightlyTotal = discountedGross + touristTax;
    return {
      key,
      listRate,
      discountedNet,
      discountedGross,
      accommodationVat,
      touristTax,
      commissionBasis,
      bookingCommission,
      commissionVatCost,
      hotelNet,
      clientNightlyTotal,
      stayClientTotal: clientNightlyTotal * nights,
      stayHotelNet: hotelNet * nights,
      nights
    };
  }

  const taxFreeDiscounted = targetNet;
  const taxFree = buildScenario({
    key: "taxFree",
    listRate:
      promotionMultiplier > 0
        ? taxFreeDiscounted / promotionMultiplier
        : taxFreeDiscounted,
    discountedNet: taxFreeDiscounted,
    discountedGross: taxFreeDiscounted,
    accommodationVat: 0,
    touristTax: 0,
    commissionBasis: 0,
    bookingCommission: 0,
    commissionVatCost: 0,
    hotelNet: targetNet
  });

  const normalVatFraction = vatRate / (1 + vatRate);
  const normalCommissionBasisMultiplier =
    input.commissionBasis === "excludingVat" ? 1 / (1 + vatRate) : 1;
  const normalRetention =
    1 -
    normalVatFraction -
    normalCommissionBasisMultiplier * commissionCostMultiplier;
  const normalGross = normalRetention > 0 ? targetNet / normalRetention : 0;
  const normalVat = normalGross * normalVatFraction;
  const normalNet = normalGross - normalVat;
  const normalCommissionBasis =
    normalGross * normalCommissionBasisMultiplier;
  const normalCommission = normalCommissionBasis * commissionRate;
  const normalCommissionVatCost =
    normalCommission * commissionVatRate * nonRecoverableShare;
  const normalTouristTax = normalNet * touristTaxRate;
  const normal = buildScenario({
    key: "normal",
    listRate:
      promotionMultiplier > 0 ? normalGross / promotionMultiplier : normalGross,
    discountedNet: normalNet,
    discountedGross: normalGross,
    accommodationVat: normalVat,
    touristTax: normalTouristTax,
    commissionBasis: normalCommissionBasis,
    bookingCommission: normalCommission,
    commissionVatCost: normalCommissionVatCost,
    hotelNet:
      normalGross - normalVat - normalCommission - normalCommissionVatCost
  });

  const clientPaysCommissionBasisMultiplier =
    input.commissionBasis === "excludingVat" ? 1 : 1 + vatRate;
  const clientPaysRetention =
    1 - clientPaysCommissionBasisMultiplier * commissionCostMultiplier;
  const clientPaysNet =
    clientPaysRetention > 0 ? targetNet / clientPaysRetention : 0;
  const clientPaysVat = clientPaysNet * vatRate;
  const clientPaysGross = clientPaysNet + clientPaysVat;
  const clientPaysCommissionBasis =
    clientPaysNet * clientPaysCommissionBasisMultiplier;
  const clientPaysCommission =
    clientPaysCommissionBasis * commissionRate;
  const clientPaysCommissionVatCost =
    clientPaysCommission * commissionVatRate * nonRecoverableShare;
  const clientPaysTouristTax = clientPaysNet * touristTaxRate;
  const clientPays = buildScenario({
    key: "clientPays",
    listRate:
      promotionMultiplier > 0
        ? clientPaysNet / promotionMultiplier
        : clientPaysNet,
    discountedNet: clientPaysNet,
    discountedGross: clientPaysGross,
    accommodationVat: clientPaysVat,
    touristTax: clientPaysTouristTax,
    commissionBasis: clientPaysCommissionBasis,
    bookingCommission: clientPaysCommission,
    commissionVatCost: clientPaysCommissionVatCost,
    hotelNet:
      clientPaysNet - clientPaysCommission - clientPaysCommissionVatCost
  });

  return {
    taxFree,
    normal,
    clientPays,
    effectiveDiscount: 1 - promotionMultiplier,
    promotionMultiplier,
    nights
  };
}

export function calculateFromClientPrice(input) {
  const clientRoomPrice = clamp(input.clientRoomPrice);
  const vatRate = clamp(input.accommodationVatRate, 0, 100) / 100;
  const touristTaxRate = clamp(input.touristTaxRate, 0, 100) / 100;
  const commissionRate = input.commissionEnabled
    ? clamp(input.commissionRate, 0, 100) / 100
    : 0;
  const commissionVatRate = clamp(input.commissionVatRate, 0, 100) / 100;
  const recoverableShare = clamp(input.commissionVatRecoverable, 0, 100) / 100;
  const nonRecoverableShare = 1 - recoverableShare;
  const promotionMultiplier = promotionFactor(
    input.promotions ?? [],
    input.stackingMode ?? "best"
  );
  const nights = Math.max(1, Math.round(clamp(input.nights, 1, 365)));

  function scenario({
    key,
    promotionBase,
    discountedNet,
    discountedGross,
    accommodationVat,
    touristTax,
    commissionBasis
  }) {
    const listRate =
      promotionMultiplier > 0
        ? promotionBase / promotionMultiplier
        : promotionBase;
    const promotionSavings = listRate - promotionBase;
    const bookingCommission = commissionBasis * commissionRate;
    const commissionVat = bookingCommission * commissionVatRate;
    const commissionVatCost = commissionVat * nonRecoverableShare;
    const clientNightlyTotal = discountedGross + touristTax;
    const hotelNet =
      clientNightlyTotal -
      touristTax -
      accommodationVat -
      bookingCommission -
      commissionVatCost;
    return {
      key,
      listRate,
      promotionSavings,
      discountedNet,
      discountedGross,
      accommodationVat,
      touristTax,
      commissionBasis,
      bookingCommission,
      commissionVat,
      commissionVatCost,
      hotelNet,
      totalDeductions:
        touristTax +
        accommodationVat +
        bookingCommission +
        commissionVatCost,
      clientNightlyTotal,
      stayClientTotal: clientNightlyTotal * nights,
      stayHotelNet: hotelNet * nights,
      nights
    };
  }

  const taxFree = scenario({
    key: "taxFree",
    promotionBase: clientRoomPrice,
    discountedNet: clientRoomPrice,
    discountedGross: clientRoomPrice,
    accommodationVat: 0,
    touristTax: 0,
    commissionBasis: 0
  });

  const normalNet = clientRoomPrice / (1 + vatRate + touristTaxRate);
  const normalVat = normalNet * vatRate;
  const normalGross = normalNet + normalVat;
  const normalTouristTax = normalNet * touristTaxRate;
  const normalCommissionBasis =
    input.commissionBasis === "excludingVat"
      ? normalNet
      : normalGross;
  const normal = scenario({
    key: "normal",
    promotionBase: normalGross,
    discountedNet: normalNet,
    discountedGross: normalGross,
    accommodationVat: normalVat,
    touristTax: normalTouristTax,
    commissionBasis: normalCommissionBasis
  });

  const clientPaysNet = clientRoomPrice;
  const clientPaysVat = clientPaysNet * vatRate;
  const clientPaysGross = clientPaysNet + clientPaysVat;
  const clientPaysTouristTax = clientPaysNet * touristTaxRate;
  const clientPaysCommissionBasis =
    input.commissionBasis === "excludingVat"
      ? clientPaysNet
      : clientPaysGross;
  const clientPays = scenario({
    key: "clientPays",
    promotionBase: clientPaysNet,
    discountedNet: clientPaysNet,
    discountedGross: clientPaysGross,
    accommodationVat: clientPaysVat,
    touristTax: clientPaysTouristTax,
    commissionBasis: clientPaysCommissionBasis
  });

  return {
    taxFree,
    normal,
    clientPays,
    effectiveDiscount: 1 - promotionMultiplier,
    promotionMultiplier,
    promotionSavings: normal.promotionSavings,
    clientRoomPrice,
    commissionEnabled: Boolean(input.commissionEnabled),
    nights
  };
}
